import {
  ArrowRight,
  Building2,
  Car,
  Loader2,
  MapPin,
  Package,
  Plus,
  RefreshCw,
  Search,
  User,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { Button, Card } from '../ui';
import './ManageInventory.css';
import { inventoryApi } from './inventoryApi.js';

/** Values must match GET /inventory/items `status` filter: available|allocated|maintenance|retired */
const INVENTORY_STATUSES = [
  { value: 'available', label: 'Available' },
  { value: 'allocated', label: 'Allocated' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'retired', label: 'Retired' },
];
const INVENTORY_TYPES = [
  'Electronics',
  'Furniture',
  'Tools',
  'Office Supplies',
  'Other',
];
const SOURCE_TYPES = ['site', 'vehicle', 'employee'];

const ManageInventory = () => {
  // Log screen view
  useScreenLogging('ManageInventory', {
    component: 'ManageInventory',
  });
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastKey, setLastKey] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pageSize = 20;

  // Filter state
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSourceType, setSelectedSourceType] = useState(null);

  // Permission state (simplified - can be enhanced later)
  const [permissions] = useState([
    'inventory.onboarding.filter',
    'inventory.onboarding.qr_scanner',
    'inventory.onboarding.add_new_inventory_item',
  ]);
  const [isPermissionLoading] = useState(false);

  useEffect(() => {
    fetchInventoryItems();
  }, []);

  // Apply local filters for immediate UI feedback
  useEffect(() => {
    applyFilters();
  }, [allItems]);

  // Debounce search and trigger API call when search term changes
  useEffect(() => {
    // Skip on initial mount
    if (allItems.length === 0) return;

    const timer = setTimeout(() => {
      fetchInventoryItems(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchInventoryItems = async (refresh = false) => {
    try {
      if (refresh) {
        setIsRefreshing(true);
        setIsLoading(true);
        setErrorMessage(null);
        setLastKey(null);
        setAllItems([]);
        setFilteredItems([]);
      }

      const filters = {};

      // Apply selected filters
      if (selectedStatus) {
        filters.status = selectedStatus;
      }
      if (selectedType) {
        filters.type = selectedType;
      }
      if (selectedSourceType) {
        filters.sourceType = selectedSourceType;
      }

      // Apply search text filters
      if (searchTerm.trim()) {
        filters.nameContains = searchTerm.trim();
      }

      console.log('Fetching inventory items with filters:', filters);

      const result = await inventoryApi.getAllInventoryItems({
        limit: pageSize,
        lastKey: refresh ? null : lastKey,
        ...filters,
      });

      if (result.success) {
        const data = result.data;
        // Handle different response formats
        let items = [];
        let hasMoreData = false;
        let nextPageToken = null;

        if (Array.isArray(data)) {
          // If data is directly an array
          items = data;
        } else if (data.items && Array.isArray(data.items)) {
          // If data has items array
          items = data.items;
          hasMoreData = data.has_more || false;
          nextPageToken =
            data.last_key ??
            data.next_page_token ??
            (typeof data.next_cursor === 'string' ? data.next_cursor : null);
        } else if (data.data && Array.isArray(data.data)) {
          // Nested data structure
          items = data.data;
          hasMoreData = data.has_more || false;
          nextPageToken =
            data.last_key ??
            data.next_page_token ??
            (typeof data.next_cursor === 'string' ? data.next_cursor : null);
        }

        setAllItems(prev => (refresh ? items : [...prev, ...items]));
        setHasMore(hasMoreData);
        setLastKey(nextPageToken);
        setIsLoading(false);
        setIsRefreshing(false);
        setErrorMessage(null);
      } else {
        const errorMsg = result.message || 'Failed to fetch inventory items';
        const errorType = result.error || 'Unknown error';
        console.error('Inventory API Error:', errorType, '-', errorMsg);
        setErrorMessage(`${errorType}: ${errorMsg}`);
        setIsLoading(false);
        setIsRefreshing(false);
      }
    } catch (error) {
      console.error('Inventory API Exception:', error);
      setErrorMessage(`Network Error: ${error.message}`);
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allItems];

    // Apply search filter
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const name = (item.name || '').toLowerCase();
        const description = (item.description || '').toLowerCase();
        const specifications = (item.specifications || '').toLowerCase();
        return (
          name.includes(query) ||
          description.includes(query) ||
          specifications.includes(query)
        );
      });
    }

    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(
        item =>
          (item.status || '').toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    // Apply type filter
    if (selectedType) {
      filtered = filtered.filter(
        item => (item.type || '').toLowerCase() === selectedType.toLowerCase()
      );
    }

    // Apply source type filter
    if (selectedSourceType) {
      filtered = filtered.filter(
        item =>
          (item.source_type || '').toLowerCase() ===
          selectedSourceType.toLowerCase()
      );
    }

    setFilteredItems(filtered);
  };

  const resetFilters = () => {
    setSelectedStatus(null);
    setSelectedType(null);
    setSelectedSourceType(null);
    setSearchTerm('');
    setFilteredItems(allItems);
  };

  const loadMoreItems = () => {
    if (hasMore && !isLoading) {
      fetchInventoryItems();
    }
  };

  const handleItemClick = itemId => {
    // Navigate to inventory item detail page
    navigate(`/inventory-item-detail/${itemId}`);
  };

  const handleAddNewItem = () => {
    navigate('/onboarding-inventory-item-onboarding');
  };

  const formatStatus = status => {
    if (!status) return 'Unknown Status';

    switch (status.toLowerCase()) {
      case 'available':
        return 'Available';
      case 'allocated':
        return 'Allocated';
      case 'maintenance':
        return 'Maintenance';
      case 'retired':
        return 'Retired';
      case 'good_condition':
        return 'Good Condition';
      case 'under_maintenance':
        return 'Under Maintenance';
      default:
        return status
          .split('_')
          .map(
            word =>
              word.charAt(0).toUpperCase() + word.substring(1).toLowerCase()
          )
          .join(' ');
    }
  };

  const getItemIcon = item => {
    const sourceType = (item.source_type || '').toLowerCase();
    const type = (item.type || '').toLowerCase();

    if (sourceType === 'site') return MapPin;
    if (sourceType === 'vehicle') return Car;
    if (sourceType === 'employee') return User;

    if (
      type.includes('electronics') ||
      type.includes('laptop') ||
      type.includes('computer')
    ) {
      return Package;
    }
    if (type.includes('furniture')) return Building2;
    if (type.includes('tools')) return Package;

    return Package;
  };

  const hasFilterPermission = () => {
    return permissions.includes('inventory.onboarding.filter');
  };

  const hasAddNewInventoryItemPermission = () => {
    return permissions.includes('inventory.onboarding.add_new_inventory_item');
  };

  if (isPermissionLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-2">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="manage-inventory-header mb-6 flex items-center justify-between">
          <div>
            <h1
              className={typography.h1}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              Manage Inventory
            </h1>
            <p className={typography.p} style={{ color: '#6B7280' }}>
              View and manage all inventory items
            </p>
          </div>
          <div className="manage-inventory-header-actions flex items-center gap-3">
            {hasAddNewInventoryItemPermission() && (
              <Button
                type="button"
                onClick={handleAddNewItem}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                style={{ color: '#ffffff' }}
              >
                <Plus className="w-4 h-4" />
                Create New Inventory Item
              </Button>
            )}
            <Button
              type="button"
              onClick={() => fetchInventoryItems(true)}
              disabled={isRefreshing}
              className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400"
              style={{ color: '#ffffff' }}
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
          </div>
        </div>

        {/* Advanced Filters Section */}
        {hasFilterPermission() && (
          <div className="manage-inventory-filters mb-6 flex items-end gap-4">
            {/* Status Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={selectedStatus || ''}
                onChange={e => setSelectedStatus(e.target.value || null)}
                className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              >
                <option value="">Select Status</option>
                {INVENTORY_STATUSES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                value={selectedType || ''}
                onChange={e => setSelectedType(e.target.value || null)}
                className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              >
                <option value="">Select Type</option>
                {INVENTORY_TYPES.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Source Type Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Type
              </label>
              <select
                value={selectedSourceType || ''}
                onChange={e =>
                  setSelectedSourceType(e.target.value || null)
                }
                className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              >
                <option value="">Select Source Type</option>
                {SOURCE_TYPES.map(sourceType => (
                  <option key={sourceType} value={sourceType}>
                    {sourceType.charAt(0).toUpperCase() +
                      sourceType.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter Actions */}
            <div className="manage-inventory-filter-actions flex gap-2">
              <Button
                type="button"
                onClick={() => {
                  resetFilters();
                  fetchInventoryItems(true);
                }}
                className="bg-gray-600 hover:bg-gray-700"
                style={{ color: '#ffffff' }}
              >
                Reset
              </Button>
              <Button
                type="button"
                onClick={() => fetchInventoryItems(true)}
                className="bg-blue-600 hover:bg-blue-700"
                style={{ color: '#ffffff' }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative w-full max-w-2xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  fetchInventoryItems(true);
                }
              }}
              placeholder="Search inventory items..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  fetchInventoryItems(true);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Results Count and Loading */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-600">
            {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}{' '}
            found
          </p>
          {isLoading && (
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          )}
        </div>

        {/* Inventory Items List */}
        <div className="space-y-4 mb-6">
          {isLoading && allItems.length === 0 ? (
            <Card.Light className="p-12">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading inventory items...</p>
              </div>
            </Card.Light>
          ) : errorMessage ? (
            <Card.Light className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 text-red-300">
                  <X className="w-full h-full" />
                </div>
                <h3
                  className={typography.h3}
                  style={{ color: '#DC2626', marginBottom: '0.5rem' }}
                >
                  Error loading inventory items
                </h3>
                <p className="text-red-600 mb-4">{errorMessage}</p>
                <Button
                  type="button"
                  onClick={() => fetchInventoryItems(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  style={{ color: '#ffffff' }}
                >
                  Retry
                </Button>
              </div>
            </Card.Light>
          ) : filteredItems.length === 0 ? (
            <Card.Light className="p-12">
              <div className="text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3
                  className={typography.h3}
                  style={{ color: '#6B7280', marginBottom: '0.5rem' }}
                >
                  No inventory items found
                </h3>
                <p className="text-gray-500 mb-4">
                  Try adjusting your filters or search terms
                </p>
                <Button
                  type="button"
                  onClick={() => {
                    resetFilters();
                    fetchInventoryItems(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  style={{ color: '#ffffff' }}
                >
                  Clear Filters
                </Button>
              </div>
            </Card.Light>
          ) : (
            <>
              {filteredItems.map(item => {
                const Icon = getItemIcon(item);
                return (
                  <Card.Glassmorphism
                    key={item.item_id || item.id}
                    className="p-4 cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleItemClick(item.item_id || item.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3
                          className={typography.h3}
                          style={{
                            color: '#000000',
                            marginBottom: '0.25rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.name || 'Unnamed Item'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {item.type || 'Unknown Type'} •{' '}
                          {formatStatus(item.status)}
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </Card.Glassmorphism>
                );
              })}
              {isLoading && allItems.length > 0 && (
                <div className="text-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
                </div>
              )}
            </>
          )}

          {/* Load More Button */}
          {hasMore && !isLoading && (
            <div className="text-center pt-4">
              <Button
                type="button"
                onClick={loadMoreItems}
                className="bg-blue-600 hover:bg-blue-700"
                style={{ color: '#ffffff' }}
              >
                Load More Items
              </Button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ManageInventory;

